import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import PandaCompanion from './PandaCompanion';

// Try to import audio files (fallback approach)
let healingMusic1, healingMusic2;
try {
  healingMusic1 = require('../assets/music/healing-music.mp3');
  healingMusic2 = require('../assets/music/healing-music2.mp3');
} catch (error) {
  console.log('Could not import audio files directly:', error);
}

const Dashboard = ({ selectedCompanion, onBack, onNavigateToZenPlay, onNavigateToReflect, onNavigateToJourney }) => {
  const [currentMood, setCurrentMood] = useState(2); // 0-4 scale
  const [userName] = useState('User'); // In real app, this would come from user input
  const [isBreathing, setIsBreathing] = useState(false);
  const [breathingPhase, setBreathingPhase] = useState('inhale');
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioRef, setAudioRef] = useState(null);
  const [currentAudioSrc, setCurrentAudioSrc] = useState(null);
  const [showPandaCompanion, setShowPandaCompanion] = useState(false);
  const [userWellnessData, setUserWellnessData] = useState(null);

  const moodEmojis = ['😢', '😕', '😐', '🙂', '😊'];
  const moodLabels = ['Terrible', 'Not Great', 'Okay', 'Good', 'Amazing'];
  
  const inspirationalQuotes = [
    "This moment is your life. Embrace it fully.",
    "Breathe in peace, breathe out stress.",
    "You are stronger than you think.",
    "Every small step forward is progress.",
    "Your feelings are valid, and you are not alone.",
    "Today is a new opportunity to grow.",
    "Be gentle with yourself, you're doing your best.",
    "Mindfulness is the key to mental freedom.",
    "You have survived 100% of your difficult days.",
    "Peace comes from within. Do not seek it without.",
    "Your mental health matters. You matter.",
    "One breath at a time, one moment at a time."
  ];
  
  const getCurrentDate = () => {
    const today = new Date();
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return today.toLocaleDateString('en-US', options);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning!';
    if (hour < 17) return 'Good Afternoon!';
    return 'Good Evening!';
  };

  const getDailyQuote = () => {
    // Get a consistent quote for today based on date
    const today = new Date();
    const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / 1000 / 60 / 60 / 24);
    return inspirationalQuotes[dayOfYear % inspirationalQuotes.length];
  };

  const handleBreathingToggle = () => {
    setIsBreathing(!isBreathing);
    if (!isBreathing) {
      setBreathingPhase('inhale');
    }
  };

  useEffect(() => {
    let interval;
    if (isBreathing) {
      interval = setInterval(() => {
        setBreathingPhase(prev => prev === 'inhale' ? 'exhale' : 'inhale');
      }, 4000); // 4 seconds inhale, 4 seconds exhale
    }
    return () => clearInterval(interval);
  }, [isBreathing]);

  // Get appropriate music source based on mood
  const getMusicSource = (mood) => {
    if (mood === 0) { // Terrible
      return "/assets/music/healing-music.mp3";
    } else if (mood === 1) { // Not Great
      return "/assets/music/healing-music2.mp3";
    }
    return null;
  };

  // Auto-play music only for Terrible and Not Great moods
  useEffect(() => {
    if (audioRef) {
      const newAudioSrc = getMusicSource(currentMood);
      
      if (newAudioSrc && currentMood <= 1) { // Only Terrible (0) and Not Great (1)
        // If the audio source changed, update it
        if (currentAudioSrc !== newAudioSrc) {
          audioRef.pause();
          audioRef.src = newAudioSrc;
          audioRef.load();
          setCurrentAudioSrc(newAudioSrc);
        }
        
        // Play the appropriate music
        audioRef.play().catch(error => {
          console.log('Auto-play prevented by browser:', error);
        });
        setIsPlaying(true);
      } else {
        // Stop music for Okay, Good, and Amazing moods
        audioRef.pause();
        setIsPlaying(false);
      }
    }
  }, [currentMood, audioRef, currentAudioSrc]);

  const handleAudioRef = (audio) => {
    if (audio && !audioRef) {
      setAudioRef(audio);
      audio.volume = 0.6; // Set comfortable volume
      audio.addEventListener('play', () => setIsPlaying(true));
      audio.addEventListener('pause', () => setIsPlaying(false));
      audio.addEventListener('ended', () => setIsPlaying(false));
      audio.addEventListener('loadstart', () => console.log('Audio loading started'));
      audio.addEventListener('canplay', () => console.log('Audio can play'));
      audio.addEventListener('error', (e) => console.log('Audio error:', e));
      
      // Set initial audio source based on current mood
      const initialSrc = getMusicSource(currentMood);
      if (initialSrc) {
        audio.src = initialSrc;
        setCurrentAudioSrc(initialSrc);
      }
    }
  };

  const toggleMusic = () => {
    if (audioRef && currentMood <= 1) { // Only allow manual control for Terrible and Not Great
      if (isPlaying) {
        audioRef.pause();
      } else {
        const audioSrc = getMusicSource(currentMood);
        if (audioSrc) {
          // Ensure correct audio source is loaded
          if (audioRef.src !== audioSrc) {
            audioRef.src = audioSrc;
            audioRef.load();
            setCurrentAudioSrc(audioSrc);
          }
          
          console.log('Attempting to play audio:', audioSrc);
          audioRef.play().catch(error => {
            console.log('Play prevented:', error);
            // Try to load the audio first
            audioRef.load();
            setTimeout(() => {
              audioRef.play().catch(e => console.log('Second attempt failed:', e));
            }, 100);
          });
        }
      }
    } else {
      console.log('Music only available for Terrible and Not Great moods');
    }
  };

  const handleOpenPandaCompanion = () => {
    setShowPandaCompanion(true);
  };

  const handleClosePandaCompanion = () => {
    setShowPandaCompanion(false);
  };

  const handleUserDataCollected = (data) => {
    setUserWellnessData(data);
    console.log('User wellness data collected:', data);
    // You can store this data in localStorage or send to a backend
    localStorage.setItem('userWellnessData', JSON.stringify(data));
    setShowPandaCompanion(false);
  };

  // Save mood data when user changes mood
  const handleMoodChange = (newMood) => {
    setCurrentMood(parseInt(newMood));
    
    // Save mood to localStorage for Journey page
    const today = new Date().toISOString().split('T')[0];
    const moodEntry = {
      date: today,
      mood: parseInt(newMood),
      timestamp: new Date().toISOString()
    };
    
    // Get existing mood history
    const existingMoodHistory = JSON.parse(localStorage.getItem('dashboardMoodHistory') || '[]');
    
    // Remove any existing entry for today
    const filteredHistory = existingMoodHistory.filter(entry => entry.date !== today);
    
    // Add new entry
    const updatedHistory = [moodEntry, ...filteredHistory].slice(0, 30); // Keep last 30 days
    
    localStorage.setItem('dashboardMoodHistory', JSON.stringify(updatedHistory));
  };

  return (
    <div className="dashboard-container">
      <div className="falling-leaves">
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
        <div className="leaf">🍃</div>
      </div>

      <div className="dashboard-content">
        {/* Header Section */}
        <div className="dashboard-header">
          <h1 className="greeting">{getGreeting()} ✨</h1>
          <p className="date">{getCurrentDate()}</p>
        </div>

        {/* Quote Section */}
        <div className="quote-section">
          <h2 className="quote-title">Be present in this moment 🌸</h2>
          <div className="quote-container">
            <p className="daily-quote">"{getDailyQuote()}"</p>
            <span className="quote-label">Quote for Today</span>
          </div>
        </div>

        {/* Breathing Exercise Section */}
        <div className="breathing-section">
          <h2 className="section-title">Take a Deep Breath 🦋</h2>
          
          <div className="breathing-exercise">
            <div 
              className={`breathing-circle ${isBreathing ? 'breathing' : ''} ${breathingPhase}`}
              onClick={handleBreathingToggle}
            >
              <span className="breathe-text">
                {isBreathing ? (breathingPhase === 'inhale' ? 'Breathe In' : 'Breathe Out') : 'Breathe'}
              </span>
            </div>
            <p className="breathing-hint">Follow the gentle rhythm</p>
          </div>
        </div>

        {/* Mood Section */}
        <div className="mood-section">
          <h2 className="section-title">How are you feeling?</h2>
          
          <div className="mood-display">
            <div className="current-mood-emoji">{moodEmojis[currentMood]}</div>
            <div className="current-mood-label">{moodLabels[currentMood]}</div>
          </div>

          <div className="mood-slider-container">
            <input
              type="range"
              min="0"
              max="4"
              value={currentMood}
              onChange={(e) => handleMoodChange(e.target.value)}
              className="mood-slider"
            />
            <div className="mood-indicators">
              {moodEmojis.map((emoji, index) => (
                <span 
                  key={index} 
                  className={`mood-indicator ${currentMood === index ? 'active' : ''}`}
                  onClick={() => handleMoodChange(index)}
                >
                  {emoji}
                </span>
              ))}
            </div>
            <p className="slider-hint">Slide to adjust your mood</p>
          </div>
        </div>

        {/* Music Recommendation Section */}
        <div className="music-section">
          <h2 className="section-title">Healing Sounds 🎵</h2>
          
          <div className="music-recommendation">
            {currentMood <= 1 ? (
              <div className="music-player-container">
                <div className="music-suggestion">
                  <div className="music-icon">🎼</div>
                  <p className="music-message">
                    {currentMood === 0 
                      ? "Deep healing music to comfort your soul" 
                      : "Gentle melodies to lift your spirits"
                    }
                  </p>
                  <p className="music-type">
                    {currentMood === 0 
                      ? "🌊 Playing: Deep Relaxation Track" 
                      : "🌸 Playing: Uplifting Harmony Track"
                    }
                  </p>
                </div>
                
                <div className="music-controls">
                  <button 
                    className={`play-pause-btn ${isPlaying ? 'playing' : 'paused'}`}
                    onClick={toggleMusic}
                  >
                    <span className="play-pause-icon">
                      {isPlaying ? '⏸️' : '▶️'}
                    </span>
                    <span className="play-pause-text">
                      {isPlaying ? 'Pause Music' : 'Play Healing Music'}
                    </span>
                  </button>
                  
                  {/* Debug info */}
                  <div style={{fontSize: '0.7rem', color: '#666', marginTop: '5px'}}>
                    Mood: {currentMood} | Audio: {audioRef ? 'Loaded' : 'Not loaded'} | Playing: {isPlaying ? 'Yes' : 'No'}
                    <br />Source: {currentAudioSrc ? currentAudioSrc.split('/').pop() : 'None'}
                  </div>
                </div>
                
                <div className="audio-player">
                  <audio 
                    ref={handleAudioRef}
                    className="custom-audio-player"
                    preload="metadata"
                    loop
                  >
                    Your browser does not support the audio element.
                  </audio>
                </div>
                
                <div className="music-status">
                  {isPlaying && (
                    <div className="now-playing">
                      <span className="music-wave">🎵</span>
                      <span className="playing-text">
                        {currentMood === 0 ? "Deep healing in progress..." : "Gentle uplift in progress..."}
                      </span>
                      <span className="music-wave">🎵</span>
                    </div>
                  )}
                </div>
                
                <div className="music-benefits">
                  <div className="benefit-item">
                    <span className="benefit-icon">🧘‍♀️</span>
                    <span className="benefit-text">Reduces stress</span>
                  </div>
                  <div className="benefit-item">
                    <span className="benefit-icon">💚</span>
                    <span className="benefit-text">Calms the mind</span>
                  </div>
                  <div className="benefit-item">
                    <span className="benefit-icon">✨</span>
                    <span className="benefit-text">Uplifts mood</span>
                  </div>
                </div>
              </div>
            ) : currentMood === 2 ? (
              <div className="music-neutral-state">
                <div className="neutral-music-icon">😌</div>
                <p className="neutral-message">
                  You're feeling okay! No need for healing music right now.
                </p>
                <div className="encouragement">
                  <span className="encouragement-text">Keep building that positive momentum!</span>
                </div>
              </div>
            ) : (
              <div className="music-positive-state">
                <div className="positive-music-icon">🎶</div>
                <p className="positive-message">
                  You're feeling great! Keep that positive energy flowing.
                </p>
                <div className="mood-celebration">
                  <span className="celebration-emoji">🌟</span>
                  <span className="celebration-text">Your inner light is shining!</span>
                  <span className="celebration-emoji">🌟</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <div className="bottom-nav">
          <div className="nav-item active">
            <span className="nav-icon">🏠</span>
            <span className="nav-label">Home</span>
          </div>
          <div className="nav-item" onClick={onNavigateToReflect}>
            <span className="nav-icon">💭</span>
            <span className="nav-label">Reflect</span>
          </div>
          <div className="nav-item" onClick={onNavigateToZenPlay}>
            <span className="nav-icon">🎮</span>
            <span className="nav-label">Zen Play</span>
          </div>
          <div className="nav-item" onClick={onNavigateToJourney}>
            <span className="nav-icon">📈</span>
            <span className="nav-label">Journey</span>
          </div>
        </div>
      </div>

      {/* Floating Panda Companion Button */}
      {!showPandaCompanion && (
        <div className="panda-chat-button" onClick={handleOpenPandaCompanion}>
          <div className="panda-button-icon">🐼</div>
          <div className="panda-button-text">Chat with Zen</div>
        </div>
      )}

      {/* Panda Companion Modal */}
      <PandaCompanion 
        isOpen={showPandaCompanion}
        onClose={handleClosePandaCompanion}
        onDataCollected={handleUserDataCollected}
      />
    </div>
  );
};

export default Dashboard;